import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { BrainCircuit, Loader2, RefreshCw, ShieldCheck, Undo2, ChevronDown, ChevronRight, Check } from "lucide-react";

const Num = ({ testid, big, label, sub }) => (
  <div data-testid={testid} className="rounded-xl border-2 border-slate-200 bg-white p-3">
    <div className="font-display text-2xl font-black leading-none text-[#1F1F1F] tabular-nums">{(big ?? 0).toLocaleString()}</div>
    <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-1.5">{label}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

const LastRun = ({ run, busy, onUndo }) => (
  <div data-testid="ai-backfill-last-run" className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex flex-wrap items-center gap-2">
    <span>
      Last run: <b>{run.numbers_marked.toLocaleString()}</b> number(s) on <b>{run.parts.toLocaleString()}</b> part(s) marked
      by {run.by} · {new Date(run.at).toLocaleString()}.
      {run.status === "undone" && (
        <span data-testid="ai-backfill-undone"> Undone by {run.undone_by} — {run.undo_put_back} put back
          {run.undo_left_alone?.length ? `, ${run.undo_left_alone.length} left alone (a person has worked on them since)` : ""}.</span>
      )}
    </span>
    {run.status !== "undone" && (
      <button data-testid="ai-backfill-undo" disabled={busy} onClick={onUndo}
        className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-300 hover:border-[#B01E28] hover:text-[#B01E28] text-xs font-bold px-2.5 h-9 transition-colors">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}Undo this run
      </button>
    )}
  </div>
);

const Samples = ({ rows }) => (
  <div data-testid="ai-backfill-samples" className="divide-y divide-slate-100 border-t border-slate-100">
    {rows.map((s, i) => (
      <div key={i} data-testid={`ai-backfill-sample-${i}`} className="px-3 py-2 text-[13px] flex flex-wrap items-center gap-2">
        <span className="font-mono font-bold text-[#B01E28]">{s.part}</span>
        <span className="text-slate-600">{s.name}</span>
        <span className="font-mono bg-slate-50 border border-slate-200 rounded px-1.5">{s.value}</span>
        {s.is_the_parts_number && <span className="text-[10px] font-bold uppercase text-slate-500">its part number</span>}
        <span className="text-slate-500">AI said: {s.ai_confidence || "no confidence given"}</span>
        {s.unsure_chars?.length > 0 && <span className="text-amber-700">unsure: {s.unsure_chars.join(" ")}</span>}
      </div>
    ))}
  </div>
);

export function AiBackfillCard() {
  const { has } = useAuth();
  const canWrite = has("manage_parts");
  const [data, setData] = useState(null);
  const [ask, setAsk] = useState(null);       // "approve" | "undo"
  const [busy, setBusy] = useState(false);
  const [openSamples, setOpenSamples] = useState(false);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/catch-up/ai-backfill/count"); setData(d); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not count"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const go = async () => {
    const which = ask;
    setAsk(null); setBusy(true);
    try {
      if (which === "approve") {
        const { data: r } = await api.post("/catch-up/ai-backfill/approve", { fingerprint: data.fingerprint });
        toast.success(`${r.run.numbers_marked} number(s) marked as AI-read — nothing else changed`);
      } else {
        const { data: r } = await api.post(`/catch-up/ai-backfill/runs/${data.last_run.id}/undo`);
        toast.success(`Put back: ${r.run.undo_put_back}`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
    load();
  };

  const c = data?.counts || {};
  return (
    <section data-testid="tool-ai-backfill" className="rounded-xl border-2 border-[#1F1F1F] bg-white overflow-hidden">
      <div className="bg-[#1F1F1F] text-white px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <BrainCircuit className="w-5 h-5 shrink-0" />
          <h2 className="font-display text-base sm:text-lg font-bold truncate">Mark old AI-read numbers</h2>
        </div>
        <button data-testid="ai-backfill-recount" onClick={load} className="text-[10px] font-mono uppercase tracking-widest bg-white/15 hover:bg-white/25 px-2 py-1 rounded shrink-0">
          <RefreshCw className="w-3 h-3 inline mr-1" />Recount
        </button>
      </div>
      <div className="p-4 space-y-4">
        <p className="text-sm text-slate-700">
          The AI's own log knows which numbers already on parts came from an AI read. This marks those as
          <b> AI-read, not checked by a person</b>, and copies the AI's confidence and unsure characters onto them.
          It <b>never changes a number</b>, a part number or serial, a name or a photo, and it never touches the AI log.
          Numbers with no record either way stay "origin unknown".
        </p>
        {!data ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Counting — nothing is written…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              <Num testid="ai-backfill-would-mark" big={data.would_mark} label="Numbers it would mark" />
              <Num testid="ai-backfill-parts" big={data.parts} label="Parts" />
              <Num testid="ai-backfill-unsure" big={c.of_those_the_ai_flagged_unsure_characters} label="AI flagged unsure characters" />
              <Num testid="ai-backfill-unknown" big={c.stays_origin_unknown} label="Stay origin unknown" sub="no record either way" />
            </div>
            <p data-testid="ai-backfill-detail" className="text-[12px] text-slate-500">
              Of those, {(c.of_those_it_is_the_parts_number || 0).toLocaleString()} are the part's own number and
              {" "}{(c.of_those_medium_or_low_confidence || 0).toLocaleString()} the AI read with medium or low confidence.
              Counted live just now. Nothing has been written.
            </p>
            {data.sample?.length > 0 && (
              <div className="rounded-lg border border-slate-200">
                <button data-testid="ai-backfill-toggle-samples" onClick={() => setOpenSamples((v) => !v)}
                  className="w-full flex items-center gap-1.5 px-3 py-2.5 text-left font-bold text-sm">
                  {openSamples ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  Samples ({data.sample.length})
                </button>
                {openSamples && <Samples rows={data.sample} />}
              </div>
            )}
            {data.last_run && <LastRun run={data.last_run} busy={busy} onUndo={() => setAsk("undo")} />}
            <div className="rounded-lg border-2 border-[#B01E28] bg-red-50 p-3">
              <p className="text-sm text-slate-700">
                Approve writes exactly the {data.would_mark.toLocaleString()} number(s) counted above — and only if the
                count is still the same when you press it. Every part it touches is logged, and the run can be undone.
              </p>
              {!canWrite && <p data-testid="ai-backfill-no-permission" className="text-[12px] text-slate-500 mt-1">Approving needs 'Manage parts'.</p>}
              <Button data-testid="ai-backfill-approve" disabled={busy || !canWrite || !data.would_mark} onClick={() => setAsk("approve")}
                className="mt-2 rounded-lg h-12 px-5 text-base font-bold bg-[#B01E28] hover:bg-[#8f1820]">
                {busy ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
                {data.would_mark ? `Approve — mark ${data.would_mark.toLocaleString()}` : "Nothing left to mark"}
              </Button>
            </div>
          </>
        )}
      </div>
      <Dialog open={!!ask} onOpenChange={(v) => !v && setAsk(null)}>
        <DialogContent data-testid="ai-backfill-confirm-dialog" className="rounded-xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#B01E28]">
              <ShieldCheck className="w-5 h-5" />{ask === "undo" ? "Undo the last run?" : "Mark them as AI-read?"}
            </DialogTitle>
            <DialogDescription>
              {ask === "undo"
                ? "Every number goes back exactly as it was — except one a person has checked or retyped since, which stays as they left it."
                : `${data?.would_mark || 0} number(s) on ${data?.parts || 0} part(s) become "AI-read, not checked". No number, name or photo changes.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-lg h-11" data-testid="ai-backfill-cancel" onClick={() => setAsk(null)}>Not yet</Button>
            <Button data-testid="ai-backfill-confirm" onClick={go} className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] font-bold">
              {ask === "undo" ? "Yes — undo it" : "Yes — mark them"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
